// DiaryRow — one row inside a day's tab.
//
// Column order matches UI_AUDIT.md §2.3 exactly (legacy `search_results.php`):
//   1. Order        — drag handle + display_order number bold
//   2. Vehicle      — make/model + reg (mono chip)
//   3. Price+Source — £amount bold + source name
//   4. Location     — address + postcode (mono)
//   5. Contact      — name + phone + payment-mode label;
//                     the WHOLE cell goes red when payment_mode = CHQ
//                     (UI_AUDIT §2.3 / §3.5 — "ffebee bg + 2px solid f44336")
//   6. Time         — timeframe pill bold
//   7. Notes        — truncated; pencil opens NotesModal
//   8. Actions      — View, Edit, Delete (Cancel — Wave B3),
//                     Highlight (popover — Wave B3)
//
// Status badge is no longer a column — the legacy diary conveys status via
// row classes only (highlight_type, cancellation_requested). All colours
// flow through CSS variables, no hex literals.
//
// Dnd-kit drag handle uses listeners/attributes; the rest of the row is
// not draggable so action buttons keep their click semantics.

import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Eye,
  GripVertical,
  Pencil,
  SquarePen,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/Button/Button';
import { ButtonGroup } from '../../../components/ui/ButtonGroup/ButtonGroup';
import { Tooltip } from '../../../components/ui/Tooltip/Tooltip';
import { cn } from '../../../utils/cn';
import { CancelBookingModal } from '../../bookings/components/CancelBookingModal';
import { HighlightPicker } from '../../bookings/components/HighlightPicker';
import { queryKeys } from '../../../api/queryKeys';
import type { BookingSummary } from '../../../types/api';
import styles from './DiaryRow.module.css';

const NOTE_TRUNCATE_AT = 60;

function truncate(text: string, n: number): string {
  return text.length <= n ? text : `${text.slice(0, n - 1).trimEnd()}…`;
}

function formatPrice(amount: number): string {
  return `£${amount.toFixed(2)}`;
}

export interface DiaryRowProps {
  booking: BookingSummary;
  /** Called with the booking when the user activates the notes editor. */
  onEditNotes: (booking: BookingSummary) => void;
  /** When true, render the row as the dnd-kit DragOverlay copy — fixed
   *  styling, no sortable wiring. */
  asOverlay?: boolean;
  /** When supplied, the diary week query key the Cancel + Highlight
   *  mutations should invalidate after a successful PATCH/POST. The
   *  parent page knows the (driver_id, week_start) pair; passing the
   *  key keeps DiaryRow ignorant of that wiring. */
  diaryWeekKey?: readonly unknown[];
  /** Driver is on holiday during this booking's timeframe. */
  holidayConflict?: boolean;
}

export function DiaryRow({
  booking,
  onEditNotes,
  asOverlay = false,
  diaryWeekKey,
  holidayConflict = false,
}: DiaryRowProps): JSX.Element {
  const queryClient = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const sortable = useSortable({ id: booking.enc_id, disabled: asOverlay });
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = sortable;

  const style = asOverlay
    ? undefined
    : { transform: CSS.Transform.toString(transform), transition };

  const fullHighlight = booking.highlight_type === 'full';
  const notesHighlight = booking.highlight_type === 'notes';
  const cancellationRequested = booking.status === 'cancellation_requested';
  const isCheque = booking.payment_mode_name === 'CHQ';

  const note = booking.notes ?? '';
  const noteShown = note.length === 0 ? '' : truncate(note, NOTE_TRUNCATE_AT);

  return (
    <div
      ref={asOverlay ? undefined : setNodeRef}
      style={style}
      role="row"
      className={cn(
        styles.row,
        fullHighlight && styles.highlightFull,
        cancellationRequested && styles.statusCancellationRequested,
        isDragging && styles.dragging,
        asOverlay && styles.overlayRow,
      )}
      aria-rowindex={booking.display_order + 1}
    >
      {/* 1. Order — drag handle + display_order */}
      <div className={styles.orderCell}>
        <button
          ref={setActivatorNodeRef}
          type="button"
          className={styles.grip}
          aria-label="Drag to reorder"
          {...(asOverlay ? {} : attributes)}
          {...(asOverlay ? {} : listeners)}
          disabled={asOverlay}
        >
          <GripVertical size={16} aria-hidden />
        </button>
        <span className={styles.orderNumber}>{booking.display_order}</span>
      </div>

      {/* 2. Vehicle — make/model + reg */}
      <div className={styles.vehicleCell}>
        <span className={styles.vehicleModel}>{booking.car_make_model}</span>
        <span className={styles.vehicleReg}>{booking.car_registration}</span>
      </div>

      {/* 3. Price & Source — £amount + source name */}
      <div className={styles.priceSourceCell}>
        <span className={styles.price}>{formatPrice(booking.price)}</span>
        <span className={styles.source}>{booking.source_name ?? '—'}</span>
      </div>

      {/* 4. Location — address + postcode */}
      <div className={styles.locationCell}>
        <span className={styles.locationLine}>{booking.effective_address.address}</span>
        <span className={styles.postcode}>{booking.effective_address.postcode}</span>
      </div>

      {/* 5. Contact — name + phone + payment-mode label.
            Whole cell goes red when payment_mode === 'CHQ'. */}
      <div className={cn(styles.contactCell, isCheque && styles.contactCheque)}>
        <span className={styles.contactName}>{booking.name}</span>
        <span className={styles.contactPhone}>{booking.phone}</span>
        {booking.payment_mode_name ? (
          <span className={styles.paymentMode}>{booking.payment_mode_name}</span>
        ) : null}
      </div>

      {/* 6. Time — timeframe pill, bold */}
      <span
        className={cn(
          styles.timeCell,
          booking.timeframe === 'AM' && styles.timeAm,
          booking.timeframe === 'AM-PM' && styles.timeAmPm,
          booking.timeframe === 'PM' && styles.timePm,
        )}
      >
        {booking.timeframe}
        {holidayConflict ? (
          <span
            className={styles.holidayFlag}
            title="Driver is on holiday during this timeframe"
            data-testid="diary-holiday-conflict"
          >
            Holiday
          </span>
        ) : null}
      </span>

      {/* 7. Notes — truncated + pencil */}
      <div className={styles.notesCell}>
        {note.length === 0 ? (
          <span className={styles.notesEmpty}>No notes</span>
        ) : note.length <= NOTE_TRUNCATE_AT ? (
          <span className={cn(styles.notesText, notesHighlight && styles.notesHighlight)}>
            {noteShown}
          </span>
        ) : (
          <Tooltip content={note}>
            <span className={cn(styles.notesText, notesHighlight && styles.notesHighlight)}>
              {noteShown}
            </span>
          </Tooltip>
        )}
        <button
          type="button"
          className={styles.notesEdit}
          aria-label={note.length === 0 ? 'Add notes' : 'Edit notes'}
          onClick={() => onEditNotes(booking)}
        >
          {note.length === 0 ? (
            <SquarePen size={14} aria-hidden />
          ) : (
            <Pencil size={14} aria-hidden />
          )}
        </button>
      </div>

      {/* 8. Actions — View | Edit | Delete (placeholder) | Highlight (placeholder) */}
      <div className={styles.actions}>
        <ButtonGroup>
          <Link
            to={`/collections/bookings/${encodeURIComponent(booking.enc_id)}`}
            aria-label="View booking"
            style={{ display: 'inline-flex' }}
          >
            <Button variant="ghost" size="sm" icon={Eye} aria-label="View booking" />
          </Link>
          <Link
            to={`/collections/bookings/${encodeURIComponent(booking.enc_id)}/edit`}
            aria-label="Edit booking"
            style={{ display: 'inline-flex' }}
          >
            <Button variant="ghost" size="sm" icon={Pencil} aria-label="Edit booking" />
          </Link>
          {/* Cancel — opens CancelBookingModal (POST /api/v1/collections/bookings/cancel). */}
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            aria-label="Cancel booking"
            onClick={() => setCancelOpen(true)}
            disabled={asOverlay}
          />
          {/* Highlight — popover with None / Full / Notes options
              (PATCH /api/v1/collections/bookings/:enc_id { highlight_type }). */}
          <HighlightPicker
            encId={booking.enc_id}
            current={booking.highlight_type}
            invalidateKeys={diaryWeekKey ? [diaryWeekKey] : []}
          />
        </ButtonGroup>
      </div>

      {/* Cancel modal lives inside the row so the modal closes when
          the row unmounts (e.g. after a successful cancel removes
          the row from the diary). */}
      <CancelBookingModal
        open={cancelOpen}
        encId={booking.enc_id}
        reference={booking.reference}
        onClose={() => setCancelOpen(false)}
        onCancelled={() => {
          if (diaryWeekKey) {
            void queryClient.invalidateQueries({ queryKey: diaryWeekKey });
          }
          void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.all });
        }}
      />
    </div>
  );
}
